import React, { useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { ExternalLink, Database, FileSearch, ArrowRight, Info, ClipboardCopy, CheckCircle, X } from 'lucide-react';
import { toast } from 'sonner';

// TXDOT Rumble Strip Item Codes Reference
const RUMBLE_STRIP_CODES = {
  // Asphalt rumble strips
  '533-7001': { description: 'Rumble Strip (Asphalt) (Shoulder)', unit: 'LF', category: 'asphalt_shoulder' },
  '533-6001': { description: 'Rumble Strip (Asphalt) (Shoulder)', unit: 'LF', category: 'asphalt_shoulder' },
  '533-7002': { description: 'Rumble Strip (Asphalt) (Centerline)', unit: 'LF', category: 'asphalt_centerline' },
  '533-6002': { description: 'Rumble Strip (Asphalt) (Centerline)', unit: 'LF', category: 'asphalt_centerline' },
  // Concrete rumble strips
  '533-7003': { description: 'Rumble Strip (Concrete) (Shoulder)', unit: 'LF', category: 'concrete_shoulder' },
  '533-6003': { description: 'Rumble Strip (Concrete) (Shoulder)', unit: 'LF', category: 'concrete_shoulder' },
  '533-7004': { description: 'Rumble Strip (Concrete) (Centerline)', unit: 'LF', category: 'concrete_centerline' },
  '533-6004': { description: 'Rumble Strip (Concrete) (Centerline)', unit: 'LF', category: 'concrete_centerline' },
};

const TxdotBidHelper = ({ onApplyQuantities, isOpen, onClose }) => {
  const [step, setStep] = useState(1);
  const [quantities, setQuantities] = useState({
    asphalt_shoulder: '',
    asphalt_centerline: '',
    concrete_shoulder: '',
    concrete_centerline: ''
  });
  const [projectFilter, setProjectFilter] = useState('');
  const [copied, setCopied] = useState(false);

  // TXDOT Dashboard URLs
  const TXDOT_BID_ITEMS_URL = 'https://tableau.txdot.gov/views/OfficialandUnofficialBidItems4_8v2/OfficialUnofficialBidItems';
  const TXDOT_BID_AVERAGES_URL = 'https://tableau.txdot.gov/views/BidItemAverageCost/BidItemAvgCostDashboard';
  
  const handleCopyItemCodes = () => {
    const codes = Object.keys(RUMBLE_STRIP_CODES).join(', ');
    navigator.clipboard.writeText(codes);
    setCopied(true);
    toast.success('Item codes copied to clipboard!');
    setTimeout(() => setCopied(false), 2000);
  };

  const handleQuantityChange = (category, value) => {
    setQuantities(prev => ({
      ...prev,
      [category]: value
    }));
  };

  const handleApply = () => {
    const parsedQuantities = {
      asphalt_shoulder: parseFloat(quantities.asphalt_shoulder) || 0,
      asphalt_centerline: parseFloat(quantities.asphalt_centerline) || 0,
      concrete_shoulder: parseFloat(quantities.concrete_shoulder) || 0,
      concrete_centerline: parseFloat(quantities.concrete_centerline) || 0
    };
    
    onApplyQuantities(parsedQuantities);
    toast.success('Quantities applied to calculator!');
    onClose();
    setStep(1);
    setQuantities({
      asphalt_shoulder: '',
      asphalt_centerline: '',
      concrete_shoulder: '',
      concrete_centerline: ''
    });
  };

  const getTotalLF = () => {
    return Object.values(quantities).reduce((sum, val) => sum + (parseFloat(val) || 0), 0);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl bg-[#1e293b] border-[rgba(255,255,255,0.1)]">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold text-white flex items-center gap-2">
            <Database className="w-5 h-5 text-[#f97316]" />
            TXDOT Bid Quantity Lookup
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Progress Steps */}
          <div className="flex items-center justify-between px-4">
            {[1, 2, 3].map((s) => (
              <div key={s} className="flex items-center">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${
                  step >= s ? 'bg-[#f97316] text-white' : 'bg-[#0f172a] text-[#64748b]'
                }`}>
                  {s}
                </div>
                {s < 3 && (
                  <div className={`w-16 h-1 mx-2 ${step > s ? 'bg-[#f97316]' : 'bg-[#0f172a]'}`} />
                )}
              </div>
            ))}
          </div>

          {/* Step 1: Open Dashboard */}
          {step === 1 && (
            <div className="space-y-4">
              <div className="bg-[#0f172a] p-4 rounded-lg">
                <h3 className="text-white font-semibold mb-2 flex items-center gap-2">
                  <FileSearch className="w-4 h-4 text-[#10b981]" />
                  Step 1: Open TXDOT Bid Items Dashboard
                </h3>
                <p className="text-[#94a3b8] text-sm mb-4">
                  The official TXDOT dashboard shows bid items for upcoming lettings. 
                  Click below to open it in a new tab.
                </p>
                
                <div className="flex flex-col gap-3">
                  <a
                    href={TXDOT_BID_ITEMS_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-between p-3 bg-[#1e293b] rounded-lg hover:bg-[#334155] transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      <ExternalLink className="w-4 h-4 text-[#f97316]" />
                      <span className="text-white font-medium">Official/Unofficial Bid Items Dashboard</span>
                    </div>
                    <span className="text-xs text-[#64748b]">Updates hourly</span>
                  </a>
                  
                  <a
                    href={TXDOT_BID_AVERAGES_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-between p-3 bg-[#1e293b] rounded-lg hover:bg-[#334155] transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      <ExternalLink className="w-4 h-4 text-[#10b981]" />
                      <span className="text-white font-medium">Bid Item Averages Dashboard</span>
                    </div>
                    <span className="text-xs text-[#64748b]">Historical costs</span>
                  </a>
                </div>
              </div>

              <div className="bg-yellow-500/10 border border-yellow-500/30 p-3 rounded-lg">
                <div className="flex items-start gap-2">
                  <Info className="w-4 h-4 text-yellow-500 mt-0.5 flex-shrink-0" />
                  <p className="text-yellow-200 text-sm">
                    <strong>Tip:</strong> Use the dashboard filters to search by Control Number, Highway, or County to find your specific project.
                  </p>
                </div>
              </div>

              <Button 
                onClick={() => setStep(2)} 
                className="w-full bg-[#f97316] hover:bg-[#ea580c] text-white"
              >
                Dashboard Opened <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          )}

          {/* Step 2: Find Item Codes */}
          {step === 2 && (
            <div className="space-y-4">
              <div className="bg-[#0f172a] p-4 rounded-lg">
                <h3 className="text-white font-semibold mb-2 flex items-center gap-2">
                  <FileSearch className="w-4 h-4 text-[#10b981]" />
                  Step 2: Find Rumble Strip Items
                </h3>
                <p className="text-[#94a3b8] text-sm mb-4">
                  In the dashboard, filter by these item codes to find rumble strip quantities:
                </p>
                
                <div className="space-y-2 mb-4">
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-[#64748b] uppercase">Rumble Strip Item Codes</span>
                    <Button 
                      size="sm" 
                      variant="ghost"
                      onClick={handleCopyItemCodes}
                      className="text-[#f97316] hover:text-[#fb923c] h-7 px-2"
                    >
                      {copied ? (
                        <CheckCircle className="w-4 h-4 mr-1" />
                      ) : (
                        <ClipboardCopy className="w-4 h-4 mr-1" />
                      )}
                      {copied ? 'Copied!' : 'Copy All'}
                    </Button>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-2">
                    {Object.entries(RUMBLE_STRIP_CODES).map(([code, info]) => (
                      <div 
                        key={code}
                        className="bg-[#1e293b] p-2 rounded text-xs flex items-center justify-between"
                      >
                        <span className="font-mono text-[#f97316]">{code}</span>
                        <span className="text-[#64748b] truncate ml-2">{info.description.split('(')[1]?.replace(')', '') || info.description}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="bg-blue-500/10 border border-blue-500/30 p-3 rounded-lg">
                <div className="flex items-start gap-2">
                  <Info className="w-4 h-4 text-blue-400 mt-0.5 flex-shrink-0" />
                  <p className="text-blue-200 text-sm">
                    <strong>Search Tip:</strong> Enter &quot;533-7&quot; in the Item Code filter to see all rumble strip items. Note the quantity (LF) for each item in your project.
                  </p>
                </div>
              </div>

              <div className="flex gap-2">
                <Button 
                  onClick={() => setStep(1)} 
                  variant="outline"
                  className="flex-1 border-[#94a3b8] text-[#94a3b8]"
                >
                  Back
                </Button>
                <Button 
                  onClick={() => setStep(3)} 
                  className="flex-1 bg-[#f97316] hover:bg-[#ea580c] text-white"
                >
                  Found Quantities <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </div>
          )}

          {/* Step 3: Enter Quantities */}
          {step === 3 && (
            <div className="space-y-4">
              <div className="bg-[#0f172a] p-4 rounded-lg">
                <h3 className="text-white font-semibold mb-2 flex items-center gap-2">
                  <FileSearch className="w-4 h-4 text-[#10b981]" />
                  Step 3: Enter Quantities from TXDOT
                </h3>
                <p className="text-[#94a3b8] text-sm mb-4">
                  Enter the quantities you found in the dashboard. These will be applied to the calculator.
                </p>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs text-[#94a3b8] uppercase">Asphalt Shoulder (LF)</Label>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-xs font-mono text-[#f97316] bg-[#1e293b] px-2 py-1 rounded">533-7001</span>
                      <Input
                        type="number"
                        value={quantities.asphalt_shoulder}
                        onChange={(e) => handleQuantityChange('asphalt_shoulder', e.target.value)}
                        className="millmetrix-input"
                        placeholder="0"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <Label className="text-xs text-[#94a3b8] uppercase">Asphalt Centerline (LF)</Label>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-xs font-mono text-[#f97316] bg-[#1e293b] px-2 py-1 rounded">533-7002</span>
                      <Input
                        type="number"
                        value={quantities.asphalt_centerline}
                        onChange={(e) => handleQuantityChange('asphalt_centerline', e.target.value)}
                        className="millmetrix-input"
                        placeholder="0"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <Label className="text-xs text-[#94a3b8] uppercase">Concrete Shoulder (LF)</Label>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-xs font-mono text-[#10b981] bg-[#1e293b] px-2 py-1 rounded">533-7003</span>
                      <Input
                        type="number"
                        value={quantities.concrete_shoulder}
                        onChange={(e) => handleQuantityChange('concrete_shoulder', e.target.value)}
                        className="millmetrix-input"
                        placeholder="0"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <Label className="text-xs text-[#94a3b8] uppercase">Concrete Centerline (LF)</Label>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-xs font-mono text-[#10b981] bg-[#1e293b] px-2 py-1 rounded">533-7004</span>
                      <Input
                        type="number"
                        value={quantities.concrete_centerline}
                        onChange={(e) => handleQuantityChange('concrete_centerline', e.target.value)}
                        className="millmetrix-input"
                        placeholder="0"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {getTotalLF() > 0 && (
                <div className="bg-[#10b981]/10 border border-[#10b981]/30 p-3 rounded-lg">
                  <div className="flex items-center justify-between">
                    <span className="text-[#10b981] font-medium">Total Linear Feet</span>
                    <span className="text-2xl font-bold text-[#10b981]">
                      {getTotalLF().toLocaleString()} LF
                    </span>
                  </div>
                </div>
              )}

              <div className="flex gap-2">
                <Button 
                  onClick={() => setStep(2)} 
                  variant="outline"
                  className="flex-1 border-[#94a3b8] text-[#94a3b8]"
                >
                  Back
                </Button>
                <Button 
                  onClick={handleApply}
                  disabled={getTotalLF() === 0}
                  className="flex-1 bg-[#10b981] hover:bg-[#059669] text-white"
                >
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Apply to Calculator
                </Button>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default TxdotBidHelper;
